import { fetchMarketGroupings } from "app/api/marketGroupingsApi";
import GoogleTagDataLayer from "app/components/GoogleTagDataLayer/GoogleTagDataLayer";
import { SessionHelper } from "app/helpers/sessionHelper";
import { EventType } from "app/types/eventType";
import { MarketGroupType } from "app/types/marketGroupType";
import { UserType } from "app/types/userType";
import {
  MarketTypes,
  MTSTabTypes,
  ResponsiveScreenSizesInt,
} from "app/utils/const";
import cx from "classnames";
import React, { Component, RefObject } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import "./ModalMarketTypeGroupings.scss";

type Props = {
  marketGroupType: string;
  event: EventType;
  sportId: number;
  setMarketGroupType: Function;
  user: UserType;
};

type State = {
  marketGroupsList: MarketGroupType[];
  showLeftArrow: boolean;
  showRightArrow: boolean;
  isScrollable: boolean;
};

class ModalMarketTypeGroupings extends Component<Props, State> {
  initialFetch: boolean;
  scrollContainerRef: RefObject<HTMLDivElement>;
  resizeObserver: ResizeObserver | null = null;

  constructor(props) {
    super(props);
    this.state = {
      marketGroupsList: [],
      showLeftArrow: false,
      showRightArrow: false,
      isScrollable: false,
    };
    this.initialFetch = false;
    this.getTabs();
    this.scrollContainerRef = React.createRef();
  }
  componentDidMount() {
    this.resizeObserver = new ResizeObserver(this.checkScroll);
    if (this.scrollContainerRef.current) {
      this.resizeObserver.observe(this.scrollContainerRef.current);
    }
    window.addEventListener("resize", this.checkScroll);
    if (this.scrollContainerRef.current) {
      this.scrollContainerRef.current.addEventListener(
        "scroll",
        this.checkScroll
      );
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener("resize", this.checkScroll);
    if (this.scrollContainerRef.current) {
      this.scrollContainerRef.current.removeEventListener(
        "scroll",
        this.checkScroll
      );
    }
  }

  checkScroll = () => {
    requestAnimationFrame(() => {
      if (this.scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          this.scrollContainerRef.current;
        const isScrollable = scrollWidth > clientWidth;
        this.setState({
          showLeftArrow: isScrollable && scrollLeft > 0,
          showRightArrow:
            isScrollable && scrollLeft < scrollWidth - clientWidth,
          isScrollable: isScrollable,
        });
      }
    });
  };

  getTabs = () => {
    const { sportId, setMarketGroupType } = this.props;
    fetchMarketGroupings(sportId).then(resp => {
      if (resp.data.marketGroupsList && !SessionHelper.isAsianRoute()) {
        this.setState(
          {
            marketGroupsList: resp.data.marketGroupsList,
          },
          () => {
            if (!this.marketGroupExists()) {
              setMarketGroupType(resp.data.marketGroupsList[0].marketGroupType);
            }
            this.checkDefaultTab();
            this.checkScroll();
          }
        );
      } else {
        /**
         * BET-14443 Filters the exchange markets from the given market groups list.
         *
         * @param marketGroupsList - The list of market groups to filter.
         * @returns The filtered market groups list without exchange markets.
         */
        const filterExchangeMarkets = resp.data.marketGroupsList.filter(
          marketGroup =>
            marketGroup.marketGroupType !== MarketTypes.EXCHANGE_MARKETS
        );

        this.setState(
          {
            marketGroupsList: filterExchangeMarkets,
          },
          () => {
            if (!this.marketGroupExists()) {
              setMarketGroupType(filterExchangeMarkets[0].marketGroupType);
            }
            this.checkDefaultTab();
          }
        );
      }
    });
  };

  checkDefaultTab = () => {
    const { event, setMarketGroupType } = this.props;
    if (event?.marketPeriods?.length === 0) {
      setMarketGroupType(MTSTabTypes.ALLMARKETS);
    }
  };

  // Check if stored marketGroupType exists in list
  marketGroupExists = () => {
    const { marketGroupsList } = this.state;
    const { marketGroupType } = this.props;
    return marketGroupsList.some(marketGroup => {
      return marketGroup.marketGroupType === marketGroupType;
    });
  };

  onClickTab = (marketGroupType: string) => {
    this.props.setMarketGroupType(marketGroupType);
  };
  scrollLeft = () => {
    if (this.scrollContainerRef.current) {
      this.scrollContainerRef.current.scrollBy({
        left: -100,
        behavior: "smooth",
      });
    }
  };
  scrollRight = () => {
    if (this.scrollContainerRef.current) {
      this.scrollContainerRef.current.scrollBy({
        left: 100,
        behavior: "smooth",
      });
    }
  };

  render() {
    const { marketGroupsList, showLeftArrow, showRightArrow, isScrollable } =
      this.state;
    const { marketGroupType, user } = this.props;
    // Filter out "bet builder" if `betBuilderBetAllowed` is false
    const filteredMarketGroupsList =
      user && !user.betBuilderBetAllowed
        ? marketGroupsList.filter(
            marketGroup => marketGroup.marketGroupLabel !== "bet builder"
          )
        : marketGroupsList;
    const isMobile = window.innerWidth <= ResponsiveScreenSizesInt.mobile;
    const sportType = this.props.event?.sportType;
    // only show horizontal scroll arrows for BASKETBALL & SOCCER on desktop,
    // (BASKETBALL/SOCCER has an extra market filter so takes up more space)
    if (!isMobile) {
      return (
        <div className="unsummarized-markets-modal__market-type-groupings">
          <div
            className={`segmented-control-container ${
              isScrollable ? "scrollable" : ""
            }`}
          >
            {showLeftArrow && (
              <button className="scroll-button left" onClick={this.scrollLeft}>
                &lt;
              </button>
            )}
            <div className="segmented-control-wrapper">
              <div
                className={"segmented-control"}
                ref={this.scrollContainerRef}
              >
                {filteredMarketGroupsList.map((marketGroup, index) => (
                  <GoogleTagDataLayer
                    googleTagClickId={"market-type-groupings"}
                    dlvEventName={"DLV-Market-Type-Groupings"}
                  >
                    <div
                      id={`market-group-${index}`}
                      className={cx("segmented-control__tab", {
                        "segmented-control__tab--active":
                          marketGroupType === marketGroup.marketGroupType,
                      })}
                      onClick={() => {
                        this.onClickTab(marketGroup.marketGroupType);
                      }}
                      key={index}
                    >
                      <span
                        className={`unsummarized-markets-modal__market-type-groupings__name ${marketGroup.marketGroupType.toLowerCase()}`}
                      >
                        {marketGroup.marketGroupType === "BET_BUILDER" ||
                        marketGroup.marketGroupType === "CARDS" ? (
                          <span className="bet-builder__filter">
                            <span> {marketGroup.marketGroupLabel}</span>{" "}
                            <FormattedMessage
                              tagName="span"
                              id="betBuilderNew"
                            />{" "}
                          </span>
                        ) : (
                          marketGroup.marketGroupLabel
                        )}
                      </span>
                    </div>
                  </GoogleTagDataLayer>
                ))}
              </div>
            </div>

            {showRightArrow && (
              <button
                className="scroll-button right"
                onClick={this.scrollRight}
              >
                &gt;
              </button>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div className="unsummarized-markets-modal__market-type-groupings">
          <div className={"segmented-control"} ref={this.scrollContainerRef}>
            {filteredMarketGroupsList.map((marketGroup, index) => (
              <GoogleTagDataLayer
                googleTagClickId={"market-type-groupings"}
                dlvEventName={"DLV-Market-Type-Groupings"}
              >
                <div
                  id={`market-group-${index}`}
                  className={cx("segmented-control__tab", {
                    "segmented-control__tab--active":
                      marketGroupType === marketGroup.marketGroupType,
                  })}
                  onClick={() => {
                    this.onClickTab(marketGroup.marketGroupType);
                  }}
                  key={index}
                >
                  <span
                    className={`unsummarized-markets-modal__market-type-groupings__name ${marketGroup.marketGroupType.toLowerCase()}`}
                  >
                    {marketGroup.marketGroupType === "BET_BUILDER" ||
                    marketGroup.marketGroupType === "CARDS" ? (
                      <span className="bet-builder__filter">
                        <span> {marketGroup.marketGroupLabel}</span>{" "}
                        <FormattedMessage tagName="span" id="betBuilderNew" />{" "}
                      </span>
                    ) : (
                      marketGroup.marketGroupLabel
                    )}
                  </span>
                </div>
              </GoogleTagDataLayer>
            ))}
          </div>
        </div>
      );
    }
  }
}

const mapStateToProps = state => ({
  marketGroupType: state.competitionListModel.marketGroupType,
  user: state.userModel.user,
});

const mapDispatchToProps = dispatch => ({
  setMarketGroupType: (marketGroupType: string) => {
    dispatch.competitionListModel.setMarketGroupType(marketGroupType);
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ModalMarketTypeGroupings) as any;
